import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Shield, Undo2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { returnToAdminSession } from '../../lib/adminApi'
import { useToast } from '../../hooks/useToast'
import { useAuthStore } from '../../stores/auth'
import { Button } from '../ui'

interface ImpersonationBannerProps {
  className?: string
}

export function ImpersonationBanner({ className = '' }: ImpersonationBannerProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()
  const user = useAuthStore((state) => state.user)
  const fetchUser = useAuthStore((state) => state.fetchUser)

  const impersonation = user?.impersonation

  const returnMutation = useMutation({
    mutationFn: returnToAdminSession,
    onSuccess: async (result) => {
      queryClient.clear()
      await fetchUser()
      toast.success('Returned to admin session')
      navigate(result.redirectPath || '/admin', { replace: true })
    },
    onError: (error: any) => {
      toast.error('Failed to return to admin', error?.message || 'Please try again')
    },
  })

  if (!impersonation?.active) {
    return null
  }

  return (
    <div
      className={`flex flex-col gap-3 rounded-2xl border border-warning-500/25 bg-warning-500/10 px-4 py-3 text-sm text-content-primary md:flex-row md:items-center md:justify-between ${className}`.trim()}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-full border border-warning-500/30 bg-warning-500/15 p-2 text-warning-500">
          <Shield className="h-4 w-4" />
        </div>
        <div>
          <p className="font-medium">
            Viewing the app as {user?.email}
          </p>
          <p className="mt-1 text-content-secondary">
            Return to {impersonation.adminUser.email} when you are done testing this account.
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        loading={returnMutation.isPending}
        onClick={() => returnMutation.mutate()}
      >
        <Undo2 className="h-4 w-4" />
        Return to admin
      </Button>
    </div>
  )
}
